// components/ProfProse.tsx — ported from docs/superpowers/design-2026-04-21/messages.jsx
// Design deviation: uses parseProse → React-node-tree instead of dangerouslySetInnerHTML + regex,
// giving us safe rendering, keyboard-accessible CitationChips, and tokens-aware code spans.

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useTokens } from '../state/theme';
import { parseBlocks, type BlockNode, type ProseNode } from '../utils/parseProse';
import { CitationChip } from './CitationChip';
import { InlineImage } from './InlineImage';

export interface ProfProseProps {
  text: string;
  serif?: boolean;      // default true — serif body
  size?: number;        // override font-size
  onCite?: (id: string) => void; // citation chip click handler
  imageRefsByQuestionId?: Record<string, string | null | undefined>;
}

function renderNode(
  node: ProseNode,
  key: number | string,
  tokens: ReturnType<typeof useTokens>,
  onCite?: (id: string) => void,
  imageRefsByQuestionId?: Record<string, string | null | undefined>,
): JSX.Element | string {
  switch (node.type) {
    case 'text':
      return node.text; // React escapes automatically — no dangerouslySetInnerHTML
    case 'break':
      // Hard line break inside a paragraph (single \n). Previously a single \n
      // smeared two lines onto one; now it renders as a real <br>.
      return <br key={key} />;
    case 'bold':
      return (
        <b key={key} style={{ color: tokens.text, fontWeight: 600 }}>
          {node.children.map((child, i) => renderNode(child, i, tokens, onCite, imageRefsByQuestionId))}
        </b>
      );
    case 'italic':
      return (
        <i key={key}>
          {node.children.map((child, i) => renderNode(child, i, tokens, onCite, imageRefsByQuestionId))}
        </i>
      );
    case 'code':
      return (
        <code key={key} style={{
          fontFamily: tokens.fonts.mono,
          fontSize: '0.88em',
          background: tokens.surface2,
          padding: '1px 5px',
          borderRadius: 4,
        }}>
          {node.text}
        </code>
      );
    case 'cite':
      return (
        <CitationChip
          key={key}
          cite={{ id: node.id, argomentoName: '', principleName: node.id }}
          onClick={() => onCite?.(node.id)}
        />
      );
    case 'principleRef':
      return (
        <Link
          key={key}
          to={`/appendice/${node.argomentoId}/${node.principleId}`}
          style={{
            color:          tokens.accentInk,
            textDecoration: 'underline',
            textDecorationColor:    tokens.accentSoft,
            textDecorationThickness: 1,
            textUnderlineOffset:    2,
            fontWeight:     500,
          }}
          title="Vai al principio nell'Appendice"
        >
          {node.raw}
        </Link>
      );
    case 'image': {
      // Pre-Socio Rebuild Session A, Task 8 — render the rebuilt Prof's
      // [[image:KIND:ID]] markup via InlineImage. Renders inline-block so
      // it can sit inside our <p> wrapper without invalid HTML nesting.
      const imageRef = node.imageKind === 'question' ? imageRefsByQuestionId?.[node.id] : undefined;
      return (
        <InlineImage key={key} kind={node.imageKind} id={node.id} imageRef={imageRef ?? undefined} />
      );
    }
  }
}

/**
 * Render one BLOCK node (FP3a). `first` controls the top margin so the leading
 * block sits flush (matching the old `i === 0 ? '0'` paragraph behaviour).
 *
 * Design tokens only — dark mode comes for free because every colour reads off
 * `tokens` (resolved per theme). Mobile: tables get a horizontal-scroll wrapper
 * so a wide grid never blows past a 380px viewport; lists use a comfortable
 * line-height for dyslexia; headings are a small-caps/semibold label, not a
 * giant h1.
 */
function renderBlock(
  block: BlockNode,
  key: number,
  first: boolean,
  tokens: ReturnType<typeof useTokens>,
  onCite?: (id: string) => void,
  imageRefsByQuestionId?: Record<string, string | null | undefined>,
): JSX.Element {
  const topMargin = first ? '0' : '12px';
  const inline = (nodes: ProseNode[]) =>
    nodes.map((node, j) => renderNode(node, j, tokens, onCite, imageRefsByQuestionId));

  switch (block.type) {
    case 'paragraph':
      return (
        <p key={key} style={{ margin: `${topMargin} 0 0` }}>
          {inline(block.children)}
        </p>
      );

    case 'heading':
      // ## → level 2 (section label), ### → level 3 (sub-label). Both render as
      // a compact uppercase-tracked semibold label, NOT a giant browser h1/h2.
      return (
        <div
          key={key}
          role="heading"
          aria-level={block.level}
          style={{
            margin: `${first ? '0' : '16px'} 0 6px`,
            fontFamily: tokens.fonts.heading,
            fontSize: block.level === 2 ? 13 : 12,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: block.level === 2 ? tokens.text : tokens.textMuted,
          }}
        >
          {inline(block.children)}
        </div>
      );

    case 'list': {
      const ListTag = block.ordered ? 'ol' : 'ul';
      return (
        <ListTag
          key={key}
          style={{
            margin: `${topMargin} 0 0`,
            paddingLeft: 22,
            // Comfortable rhythm for dyslexia — generous line-height + item gap.
            lineHeight: 1.7,
          }}
        >
          {block.items.map((item, idx) => (
            <li key={idx} style={{ margin: idx === 0 ? '0' : '4px 0 0', paddingLeft: 2 }}>
              {inline(item)}
            </li>
          ))}
        </ListTag>
      );
    }

    case 'table':
      return (
        // Horizontal-scroll wrapper so a wide table stays usable on a 380px
        // phone instead of overflowing the chat bubble.
        <div
          key={key}
          style={{
            margin: `${topMargin} 0 0`,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            borderRadius: tokens.radius.sm,
            border: `1px solid ${tokens.line}`,
          }}
        >
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              fontSize: '0.94em',
              lineHeight: 1.5,
            }}
          >
            <thead>
              <tr>
                {block.header.map((cell, c) => (
                  <th
                    key={c}
                    style={{
                      textAlign: 'left',
                      padding: '8px 10px',
                      fontWeight: 600,
                      color: tokens.text,
                      background: tokens.surface2,
                      borderBottom: `1px solid ${tokens.lineStrong}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {inline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      style={{
                        padding: '8px 10px',
                        color: tokens.text,
                        borderTop: r === 0 ? 'none' : `1px solid ${tokens.line}`,
                        verticalAlign: 'top',
                      }}
                    >
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

export function ProfProse({
  text,
  serif = true,
  size,
  onCite,
  imageRefsByQuestionId,
}: ProfProseProps): JSX.Element {
  const tokens = useTokens();
  const blocks = parseBlocks(text);

  return (
    <div style={{
      fontFamily: serif ? tokens.fonts.serif : tokens.fonts.body,
      fontSize: size ?? (serif ? 16 : 15),
      lineHeight: 1.6,
      color: tokens.text,
      textWrap: 'pretty' as React.CSSProperties['textWrap'],
    }}>
      {blocks.map((block, i) =>
        renderBlock(block, i, i === 0, tokens, onCite, imageRefsByQuestionId),
      )}
    </div>
  );
}
